import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import toast from 'react-hot-toast';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

const PaymentMethodCard = () => {
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 'pm_1',
      type: 'card',
      brand: 'visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2025',
      isDefault: true,
      holderName: 'John Doe'
    }
  ]);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSetDefault = (methodId) => {
    setPaymentMethods(methods =>
      methods?.map(m => ({ ...m, isDefault: m?.id === methodId }))
    );
    toast?.success('Default payment method updated');
  };

  const handleRemove = (methodId) => {
    const method = paymentMethods?.find(m => m?.id === methodId);
    if (method?.isDefault && paymentMethods?.length > 1) {
      toast?.error('Cannot remove default payment method. Set another as default first.');
      return;
    }
    setPaymentMethods(methods => methods?.filter(m => m?.id !== methodId));
    toast?.success('Payment method removed');
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: 'CreditCard',
      mastercard: 'CreditCard',
      amex: 'CreditCard',
      discover: 'CreditCard'
    };
    return icons?.[brand] || 'CreditCard';
  };

  const getCardBrandColor = (brand) => {
    const colors = {
      visa: 'text-blue-600',
      mastercard: 'text-orange-600',
      amex: 'text-green-600',
      discover: 'text-purple-600'
    };
    return colors?.[brand] || 'text-primary';
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="CreditCard" size={20} />
            Payment Methods
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Manage your saved payment methods</p>
        </div>
        {/* Add Method button — gradient */}
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 100,
            border: 'none', cursor: 'pointer',
            background: GRAD, color: '#fff',
            fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
            boxShadow: GRAD_SHADOW,
            transition: 'filter 0.15s, transform 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
        >
          <Icon name="Plus" size={14} color="#fff" />
          Add Method
        </button>
      </div>

      {/* Add Payment Method Form */}
      {showAddForm && (
        <div className="bg-muted/30 border border-border rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Add New Payment Method</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Card Number</label>
              <input
                type="text"
                placeholder="1234 5678 9012 3456"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Expiry Date</label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">CVV</label>
                <input
                  type="text"
                  placeholder="123"
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Cardholder Name</label>
              <input
                type="text"
                placeholder="John Doe"
                className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div className="flex items-center gap-3">
              {/* Save Card — gradient */}
              <button
                onClick={() => {
                  toast?.success('Payment method added successfully');
                  setShowAddForm(false);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 100,
                  border: 'none', cursor: 'pointer',
                  background: GRAD, color: '#fff',
                  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                  boxShadow: GRAD_SHADOW,
                  transition: 'filter 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
              >
                Save Card
              </button>
              <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Methods List */}
      <div className="space-y-4">
        {paymentMethods?.length > 0 ? (
          paymentMethods?.map((method) => (
            <div
              key={method?.id}
              className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-background border border-border">
                  <Icon name={getCardIcon(method?.brand)} size={24} className={getCardBrandColor(method?.brand)} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground capitalize">{method?.brand} •••• {method?.last4}</p>
                    {method?.isDefault && (
                      <span style={{
                        padding: '2px 10px', borderRadius: 100,
                        background: GRAD, color: '#fff',
                        fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                      }}>
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">Expires {method?.expiryMonth}/{method?.expiryYear}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{method?.holderName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!method?.isDefault && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(method?.id)}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  onClick={() => handleRemove(method?.id)}
                />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Icon name="CreditCard" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-4">No payment methods saved</p>
            {/* Add First Method — gradient */}
            <button
              onClick={() => setShowAddForm(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 20px', borderRadius: 100,
                border: 'none', cursor: 'pointer',
                background: GRAD, color: '#fff',
                fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                boxShadow: GRAD_SHADOW,
                transition: 'filter 0.15s, transform 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
            >
              <Icon name="Plus" size={14} color="#fff" />
              Add Your First Payment Method
            </button>
          </div>
        )}
      </div>

      {/* Payment Method Info */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Secure Payment Storage</p>
            <p>Your payment information is securely stored and encrypted. We never store your full card details. All transactions are processed through Razorpay's secure payment gateway.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodCard;